//Node Modules
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilValueLoadable } from "recoil";
import { DateTime } from "luxon";
import _ from "lodash";

//BinaryForge Components
import DashboardBarChart from "./DashboardBarChart";
import { Note } from "../helpers";

//3rd Party Components
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";

//Atoms
import { eventSummarySelector, devicesStateSelector } from "../../atoms/dashboard";

//Helpers
import { chartStyleConfig } from "../../config/chart";
import { RSSI_BANDS, getRssiBand } from "../../helpers/Device";

//Other
import { nav } from "../../config/navigation";

const devicesHealthUrl = (healthKey) => `${nav.device.base}/${nav.device.management}?health=${healthKey}`;

const palette = chartStyleConfig.doughnut.backgroundColours.default;

const ChartState = ({ state, error, isEmpty, emptyMessage, children }) => {
	if (state === "loading")
		return (
			<div className="flex jContent-center">
				<ProgressSpinner strokeWidth="4" style={{ width: "48px", height: "48px" }} />
			</div>
		);
	if (state === "hasError")
		return <Note messageKey={`Error: ${error?.message ?? error}`} icon="pi-exclamation-circle fontColour-error" />;
	if (isEmpty)
		return <Note messageKey={emptyMessage} icon="pi-info-circle fontColour-info" wrapperStyle="aItems-center" />;
	return children;
};

//Fixed colour per event type so a type keeps its colour across ranges and page loads,
//regardless of which other types happen to appear; hash fallback for unknown types
const EVENT_TYPE_COLOURS = {
	OUT_OF_COMMS: palette[0],
	SENSOR_INPUT_FLEXIPAD: palette[1],
	ALARM_ACTIVE: palette[2],
	BATTERY_LOW: palette[3],
	SENSOR_INPUT_FLOWSTOP: palette[4],
	SENSOR_INPUT_RANGER: palette[5],
	MAINS_POWER_FAIL: palette[6],
	RELAY: "hsl(44, 100%, 50%)",
};
const EVENT_TYPE_ORDER = Object.keys(EVENT_TYPE_COLOURS);

const colourForEventType = (eventType) => {
	if (EVENT_TYPE_COLOURS[eventType]) return EVENT_TYPE_COLOURS[eventType];
	const hash = [...String(eventType)].reduce((acc, char) => acc + char.charCodeAt(0), 0);
	return palette[hash % palette.length];
};

const TREND_RANGES = {
	"24h": { buckets: 24, unit: "hours", startOf: "hour", format: "HH:00" },
	"7d": { buckets: 7, unit: "days", startOf: "day", format: "d MMM" },
	"30d": { buckets: 30, unit: "days", startOf: "day", format: "d MMM" },
};

export const EventTrendChart = () => {
	const { t } = useTranslation();
	const { state, contents } = useRecoilValueLoadable(eventSummarySelector);
	const [range, setRange] = useState("24h");

	const rangeOptions = [
		{ value: "24h", label: t("dashboard.charts.trendRange.day") },
		{ value: "7d", label: t("dashboard.charts.trendRange.week") },
		{ value: "30d", label: t("dashboard.charts.trendRange.month") },
	];

	let labels = [];
	let datasets = [];

	if (state === "hasValue") {
		const { buckets, unit, startOf, format } = TREND_RANGES[range];
		const start = DateTime.now()
			.minus({ [unit]: buckets - 1 })
			.startOf(startOf);
		labels = _.range(buckets).map((offset) => start.plus({ [unit]: offset }).toFormat(format));

		//Archive events are completed; live events are ongoing - both count towards the trend
		const events = [...(contents.archiveEvents ?? []), ...(contents.liveEvents ?? [])].filter(
			(event) => DateTime.fromISO(event.updatedAt) >= start,
		);

		const eventsByType = _.groupBy(events, "eventType");
		datasets = _.sortBy(Object.entries(eventsByType), ([eventType]) => {
			const order = EVENT_TYPE_ORDER.indexOf(eventType);
			return order === -1 ? EVENT_TYPE_ORDER.length : order;
		}).map(([eventType, typeEvents]) => {
			const countsByBucket = _.countBy(typeEvents, (event) =>
				DateTime.fromISO(event.updatedAt).startOf(startOf).toFormat(format),
			);
			return {
				label: t("events.eventType.eventType", { context: eventType }),
				data: labels.map((label) => countsByBucket[label] ?? 0),
				backgroundColor: colourForEventType(eventType),
			};
		});
	}

	return (
		<>
			<div className="flex jContent-end marginBottom-small">
				<SelectButton
					value={range}
					options={rangeOptions}
					onChange={(e) => {
						if (e.value) setRange(e.value);
					}}
				/>
			</div>
			<ChartState
				state={state}
				error={contents}
				isEmpty={datasets.length === 0}
				emptyMessage={t("dashboard.charts.trendEmpty")}
			>
				<DashboardBarChart labels={labels} datasets={datasets} stacked />
			</ChartState>
		</>
	);
};

export const RssiDistributionChart = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { state, contents } = useRecoilValueLoadable(devicesStateSelector);

	let labels = [];
	let datasets = [];

	if (state === "hasValue") {
		const counts = Object.fromEntries(RSSI_BANDS.map((band) => [band.key, 0]));
		contents.forEach((device) => {
			const band = getRssiBand(device.reportedState?.wifi?.rssi);
			if (band) counts[band.key] += 1;
		});

		labels = RSSI_BANDS.map((band) => t(`device.state.wifiRssiQuality.${band.key}`));
		datasets = [{ data: RSSI_BANDS.map((band) => counts[band.key]), backgroundColor: palette }];
	}

	const handleBarClick = useCallback(
		(datasetIndex, index) => navigate(devicesHealthUrl(`rssi-${RSSI_BANDS[index].key}`)),
		[navigate],
	);

	return (
		<ChartState state={state} error={contents}>
			<DashboardBarChart labels={labels} datasets={datasets} onBarClick={handleBarClick} />
		</ChartState>
	);
};

const BATTERY_BANDS = [
	{ label: "0-20%", key: "battery-low", min: -Infinity, max: 20 },
	{ label: "21-40%", key: "battery-21-40", min: 20, max: 40 },
	{ label: "41-60%", key: "battery-41-60", min: 40, max: 60 },
	{ label: "61-80%", key: "battery-61-80", min: 60, max: 80 },
	{ label: "81-100%", key: "battery-81-100", min: 80, max: Infinity },
];

export const BatteryDistributionChart = () => {
	const navigate = useNavigate();
	const { state, contents } = useRecoilValueLoadable(devicesStateSelector);

	let labels = [];
	let datasets = [];

	if (state === "hasValue") {
		const counts = BATTERY_BANDS.map(
			(band) =>
				contents.filter((device) => {
					const percentage = Number(device.reportedState?.battery?.percentage);
					return Number.isFinite(percentage) && percentage > band.min && percentage <= band.max;
				}).length,
		);

		labels = BATTERY_BANDS.map((band) => band.label);
		//Low battery band highlighted in the error colour
		datasets = [{ data: counts, backgroundColor: ["hsl(345, 100%, 39%)", ...palette] }];
	}

	const handleBarClick = useCallback(
		(datasetIndex, index) => navigate(devicesHealthUrl(BATTERY_BANDS[index].key)),
		[navigate],
	);

	return (
		<ChartState state={state} error={contents}>
			<DashboardBarChart labels={labels} datasets={datasets} onBarClick={handleBarClick} />
		</ChartState>
	);
};
